import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Chip,
  Stepper,
  Step,
  StepLabel,
  Divider,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getSalesOrderDetail } from '../services/api';
import { getTatStatusColor, getTatStatusLabel, formatDate } from '../utils/tatCalculator';
import CourierTracking from './CourierTracking';
import PODViewer from './PODViewer';

const SalesOrderDetail = () => {
  const { soNo: encodedSoNo } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Decode the SO number from URL to handle special characters like slashes
  const soNo = encodedSoNo ? decodeURIComponent(encodedSoNo) : '';
  const [soData, setSoData] = useState(null);
  const [items, setItems] = useState([]);
  const [courierData, setCourierData] = useState(null);
  const [tatStages, setTatStages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [podViewerOpen, setPodViewerOpen] = useState(false);

  useEffect(() => {
    if (soNo) {
      fetchSalesOrderDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soNo]);

  const fetchSalesOrderDetail = async () => {
    if (!soNo) return;
    setLoading(true);
    try {
      const response = await getSalesOrderDetail(soNo);
      setSoData(response.data.so_data);
      setItems(response.data.items || []);
      setCourierData(response.data.courier_data);
      setTatStages(response.data.tat_stages);
    } catch (error) {
      console.error('Error fetching SO detail:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!soData) {
      alert('No data to download');
      return;
    }

    // Escape CSV values (handle commas, quotes, newlines)
    const escapeCSV = (value) => {
      if (value === null || value === undefined) return '';
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    // Create CSV content with SO details and items
    const csvLines = [];

    // Add SO Details section
    csvLines.push('Sales Order Details');
    csvLines.push('');
    csvLines.push([
      'SO No', 'SO Date', 'Customer', 'Site', 'Channel', 'SO Qty',
      'Dispatch Date', 'Dispatch Qty', 'GRC Date', 'GRC Qty',
      'Status', 'SO Status', 'SO Item Status', 'AWB', 'Courier',
      'Invoice No', 'Reference Site', 'Created By', 'Authorized By',
      'Brand', 'Style', 'Color', 'Remarks'
    ].map(escapeCSV).join(','));
    
    csvLines.push([
      soData.SO_NO || '',
      soData.SO_DATE ? formatDate(soData.SO_DATE) : '',
      soData.CUSTOMER_NAME || '',
      soData.OWNER_SITE || '',
      soData.SO_CHANNEL || '',
      soData.SO_QTY || '',
      soData.DISPATCH_DATE ? formatDate(soData.DISPATCH_DATE) : '',
      soData.DISPATCH_QTY || '',
      soData.GRC_DATE ? formatDate(soData.GRC_DATE) : '',
      soData.GRC_QTY || '',
      soData.STATUS || '',
      soData.SO_STATUS || '',
      soData.SO_ITEM_STATUS || '',
      soData.UC_AWB || soData.DISPATCH_AWB || soData.AWB || '',
      soData.COURIER || soData.COURIER_NAME || '',
      soData.INVOICE_NO || '',
      soData.REFERENCE_SITE || '',
      soData.CREATED_BY || '',
      soData.AUTHORIZED_BY || '',
      soData.BRAND || '',
      soData.STYLE || '',
      soData.COLOR || '',
      soData.REMARKS || ''
    ].map(escapeCSV).join(','));

    // Add Items section
    if (items && items.length > 0) {
      csvLines.push('');
      csvLines.push('Items');
      csvLines.push('');
      csvLines.push([
        'Barcode', 'Brand', 'Style', 'Color', 'Department', 'Seasons',
        'SO Qty', 'Dispatch Qty', 'GRC Qty', 'SO Item Status', 'Status'
      ].map(escapeCSV).join(','));

      items.forEach(item => {
        csvLines.push([
          item.barcode || '',
          item.brand || '',
          item.style || '',
          item.color || '',
          item.department || '',
          item.seasons || '',
          item.so_qty || '',
          item.dispatch_qty || '',
          item.grc_qty || '',
          item.uc_sale_order_item_status || item.so_item_status || item.status || ''
        ].map(escapeCSV).join(','));
      });
    }

    // Create blob and download
    const csvContent = csvLines.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `sales_order_${soData.SO_NO || 'detail'}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Helper function to format empty/null values
  const formatValue = (value) => {
    if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
      return '-';
    }
    return value;
  };

  // Helper function to format expected delivery date: "06 Jan 2026, 11:59 pm" (end of day)
  const formatExpectedDelivery = (expectedDelivery, courierName) => {
    if (!expectedDelivery) return 'N/A';
    
    const courier = (courierName || '').toLowerCase();
    const isBlueDart = courier.includes('bluedart') || courier.includes('blue dart');
    const isEkart = courier.includes('ekart');
    
    try {
      const dateStr = String(expectedDelivery).trim();
      let dateObj;
      
      // Try ISO format first
      if (dateStr.includes('T') || dateStr.includes('Z')) {
        dateObj = new Date(dateStr);
      } else {
        // Try "DD Month YYYY" format (Bluedart)
        const months = {
          'january': 0, 'february': 1, 'march': 2, 'april': 3, 'may': 4, 'june': 5,
          'july': 6, 'august': 7, 'september': 8, 'october': 9, 'november': 10, 'december': 11
        };
        
        const datePattern = /^(\d{1,2})\s+(\w+)\s+(\d{4})/;
        const match = dateStr.match(datePattern);
        
        if (match) {
          const day = parseInt(match[1], 10);
          const monthName = match[2].toLowerCase();
          const year = parseInt(match[3], 10);
          
          if (months[monthName] !== undefined && !isNaN(day) && !isNaN(year)) {
            dateObj = new Date(year, months[monthName], day);
          }
        } else if (isEkart && /^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
          // Try "DD-MM-YYYY" format (Ekart edd format like "07-01-2026")
          const parts = dateStr.split('-');
          if (parts.length === 3) {
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1; // JavaScript months are 0-indexed
            const year = parseInt(parts[2], 10);
            dateObj = new Date(year, month, day);
          }
        } else {
          dateObj = new Date(dateStr);
        }
      }
      
      if (dateObj && !isNaN(dateObj.getTime())) {
        // If time is midnight (00:00:00), set to end of day (23:59:59)
        if (dateObj.getHours() === 0 && dateObj.getMinutes() === 0 && dateObj.getSeconds() === 0) {
          dateObj.setHours(23, 59, 59);
        }
        
        const day = dateObj.getDate().toString().padStart(2, '0');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[dateObj.getMonth()];
        const year = dateObj.getFullYear();
        const hours24 = dateObj.getHours();
        const minutes = dateObj.getMinutes().toString().padStart(2, '0');
        
        // Format time (12-hour format with am/pm)
        const period = hours24 >= 12 ? 'pm' : 'am';
        const displayHours = hours24 % 12 || 12;
        const formattedTime = `${displayHours.toString().padStart(2, '0')}:${minutes} ${period}`;
        
        return `${day} ${month} ${year}, ${formattedTime}`;
      }
      
      // Fallback to default formatDate
      return formatDate(expectedDelivery);
    } catch (e) {
      return formatDate(expectedDelivery);
    }
  };

  const InfoRow = ({ label, value }) => {
    return React.createElement(Grid, { container: true, spacing: 2, sx: { mb: 1.5 } },
      React.createElement(Grid, { item: true, xs: 4 },
        React.createElement(Typography, {
          variant: 'body2',
          color: 'textSecondary',
          fontWeight: 'medium'
        }, label)
      ),
      React.createElement(Grid, { item: true, xs: 8 },
        React.createElement(Typography, {
          variant: 'body2'
        }, formatValue(value))
      )
    );
  };

  if (!soNo) {
    return React.createElement(Container, { maxWidth: 'lg', sx: { py: 4 } },
      React.createElement(Typography, { variant: 'h6', color: 'error' }, 'Invalid Sales Order Number'),
      React.createElement(Typography, { variant: 'body2', color: 'text.secondary', sx: { mt: 1 } }, 
        'The sales order number is missing or invalid. Please go back and try again.')
    );
  }

  if (loading) {
    return React.createElement(Box, {
      sx: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }
    },
      React.createElement(CircularProgress, null)
    );
  }

  if (!soData) {
    return React.createElement(Container, { maxWidth: 'lg', sx: { py: 4 } },
      React.createElement(Typography, { variant: 'h6', color: 'error' }, 'Sales Order not found'),
      React.createElement(Typography, { variant: 'body2', color: 'text.secondary', sx: { mt: 1 } }, 
        `Sales Order "${soNo}" could not be found.`)
    );
  }

  const steps = [
    { label: 'SO Created', stage: 'stage0' },
    { label: 'Authorized', stage: 'stage0' },
    { label: 'Ready to Ship', stage: 'stage1' },
    { label: 'Picked Up', stage: 'stage2' },
    { label: 'Delivered', stage: 'stage3' },
    { label: 'Goods Received', stage: 'stage4' }
  ];

  const activeStep = soData.GRC_DATE ? 6 
    : (courierData?.deliveredTime ? 5
    : (courierData?.pickupDate ? 4
    : (soData.DISPATCH_DATE ? 3
    : (soData.AUTHORIZED_ON ? 2
    : (soData.SO_DATE ? 1 : 0)))));

  return React.createElement(Container, {
    maxWidth: 'lg',
    sx: {
      px: { xs: 2, sm: 3, md: 4 }
    }
  },
    React.createElement(Box, {
      sx: {
        mb: 4,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 2
      }
    },
      React.createElement(Button, {
        startIcon: React.createElement(ArrowBackIcon, null),
        onClick: () => {
          // Preserve query params when navigating back
          const queryString = location.search || '';
          navigate(`/explore/sora/sales-orders${queryString}`);
        },
        sx: {
          color: 'text.primary',
          textTransform: 'none',
          '&:hover': {
            backgroundColor: 'transparent',
            opacity: 0.7,
          }
        }
      }, 'Back to List'),
      React.createElement(Button, {
        variant: 'outlined',
        startIcon: React.createElement(DownloadIcon, null),
        onClick: handleDownload,
        disabled: !soData || loading,
        sx: {
          borderColor: 'divider',
          color: 'text.primary',
          textTransform: 'none',
          borderRadius: 2,
          fontWeight: 500,
          height: '40px',
          '&:hover': {
            borderColor: 'text.primary',
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
          },
          '&:disabled': {
            opacity: 0.5
          }
        }
      }, 'Download CSV')
    ),
    React.createElement(Box, { sx: { mb: { xs: 4, sm: 6 } } },
      React.createElement(Typography, {
        variant: 'h1',
        sx: {
          fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
          fontWeight: 600,
          letterSpacing: '-0.02em',
          mb: 1,
          lineHeight: 1.1
        }
      }, `Sales Order`),
      React.createElement(Typography, {
        variant: 'h2',
        sx: {
          fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
          fontWeight: 600,
          letterSpacing: '-0.01em',
          color: 'text.secondary',
          lineHeight: 1.1
        }
      }, soData.SO_NO)
    ),
    
    // TAT Stages Timeline
    React.createElement(Box, {
      sx: {
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        p: { xs: 3, sm: 4 },
        mb: 4,
        backgroundColor: 'background.paper'
      }
    },
      React.createElement(Typography, {
        variant: 'h6',
        sx: {
          mb: 4,
          fontWeight: 600,
          fontSize: '1.25rem'
        }
      }, 'Order Lifecycle Timeline'),
      React.createElement(Stepper, {
        activeStep: activeStep,
        alternativeLabel: true
      },
        steps.map((step, index) => 
          React.createElement(Step, { key: index },
            React.createElement(StepLabel, null, step.label)
          )
        )
      ),
      tatStages && React.createElement(Box, { sx: { mt: 3 } },
        React.createElement(Typography, {
          variant: 'subtitle1',
          gutterBottom: true
        }, 'TAT Status by Stage:'),
        React.createElement(Grid, { container: true, spacing: 2, sx: { mt: 1 } },
          Object.entries(tatStages).map(([key, stage]) =>
            React.createElement(Grid, { item: true, xs: 12, sm: 6, md: 3, key: key },
              React.createElement(Box, {
                sx: {
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                  backgroundColor: 'background.paper'
                }
              },
                React.createElement(Typography, {
                  variant: 'body2',
                  color: 'text.secondary',
                  sx: {
                    mb: 1.5,
                    fontSize: '0.8125rem',
                    fontWeight: 500
                  }
                }, stage.name),
                React.createElement(Chip, {
                  label: getTatStatusLabel(stage.status),
                  size: 'small',
                  sx: {
                    mt: 1,
                    mb: 1.5,
                    backgroundColor: getTatStatusColor(stage.status) === 'success' ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
                    border: '1px solid',
                    borderColor: 'divider',
                    color: 'text.primary',
                    fontWeight: 500,
                    borderRadius: 2
                  }
                }),
                React.createElement(Box, {
                  sx: { mt: 1.5 }
                },
                  React.createElement(Typography, {
                    variant: 'caption',
                    color: 'text.secondary',
                    sx: { display: 'block', mb: 0.5, fontSize: '0.75rem' }
                  }, `TAT Limit: ${stage.limit_hours !== null && stage.limit_hours !== undefined ? `${stage.limit_hours} hrs` : '-'}`),
                  React.createElement(Typography, {
                    variant: 'caption',
                    sx: { 
                      display: 'block', 
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      color: stage.actual_hours !== null && stage.actual_hours !== undefined
                        ? (stage.limit_hours && stage.actual_hours > stage.limit_hours ? 'error.main' : 'success.main')
                        : 'text.secondary'
                    }
                  }, `Actual: ${stage.actual_hours !== null && stage.actual_hours !== undefined ? `${stage.actual_hours} hrs` : '-'}`)
                )
              )
            )
          )
        )
      )
    ),

    // SO Details
    React.createElement(Box, {
      sx: {
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        p: { xs: 3, sm: 4 },
        mb: 4,
        backgroundColor: 'background.paper'
      }
    },
      React.createElement(Typography, {
        variant: 'h6',
        sx: {
          mb: 3,
          fontWeight: 600,
          fontSize: '1.25rem'
        }
      }, 'Sales Order Details'),
      React.createElement(Divider, { sx: { mb: 3 } }),
      React.createElement(InfoRow, { label: 'SO Number', value: soData.SO_NO }),
      React.createElement(InfoRow, { label: 'SO Date', value: soData.SO_DATE ? formatDate(soData.SO_DATE) : null }),
      React.createElement(InfoRow, { label: 'Customer', value: soData.CUSTOMER_NAME }),
      React.createElement(InfoRow, { label: 'Owner Site', value: soData.OWNER_SITE }),
      React.createElement(InfoRow, { label: 'Reference Site', value: soData.REFERENCE_SITE }),
      React.createElement(InfoRow, { label: 'Channel', value: soData.SO_CHANNEL }),
      React.createElement(InfoRow, { label: 'Quantity', value: soData.SO_QTY }),
      React.createElement(InfoRow, { label: 'Created By', value: soData.CREATED_BY }),
      React.createElement(InfoRow, { label: 'Authorized By', value: soData.AUTHORIZED_BY }),
      React.createElement(InfoRow, { label: 'Authorized On', value: soData.AUTHORIZED_ON ? formatDate(soData.AUTHORIZED_ON) : null }),
      React.createElement(InfoRow, { label: 'Remarks', value: soData.REMARKS })
    ),

    // Items/Barcodes Section
    items && items.length > 0 && React.createElement(Box, {
      sx: {
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        p: { xs: 3, sm: 4 },
        mb: 4,
        backgroundColor: 'background.paper'
      }
    },
      React.createElement(Typography, {
        variant: 'h6',
        sx: {
          mb: 3,
          fontWeight: 600,
          fontSize: '1.25rem'
        }
      }, `Items (${items.length})`),
      React.createElement(Divider, { sx: { mb: 3 } }),
      React.createElement(Accordion, {
        defaultExpanded: true,
        sx: {
          boxShadow: 'none',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          '&:before': {
            display: 'none'
          }
        }
      },
        React.createElement(AccordionSummary, {
          expandIcon: React.createElement(ExpandMoreIcon, null),
          sx: {
            '& .MuiAccordionSummary-content': {
              my: 1
            }
          }
        },
          React.createElement(Typography, {
            variant: 'body1',
            sx: {
              fontWeight: 500
            }
          }, `View All Items (${items.length})`)
        ),
        React.createElement(AccordionDetails, {
          sx: {
            p: 0,
            pt: 2
          }
        },
          React.createElement(TableContainer, {
            component: Paper,
            sx: {
              border: 'none',
              boxShadow: 'none',
              maxHeight: 500,
              overflow: 'auto'
            }
          },
            React.createElement(Table, {
              size: 'small',
              stickyHeader: true
            },
              React.createElement(TableHead, null,
                React.createElement(TableRow, null,
                  React.createElement(TableCell, {
                    align: 'center',
                    sx: {
                      fontWeight: 600,
                      backgroundColor: 'background.paper',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      textAlign: 'center',
                      verticalAlign: 'middle'
                    }
                  }, 'Barcode'),
                  React.createElement(TableCell, {
                    align: 'center',
                    sx: {
                      fontWeight: 600,
                      backgroundColor: 'background.paper',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      textAlign: 'center',
                      verticalAlign: 'middle'
                    }
                  }, 'Brand'),
                  React.createElement(TableCell, {
                    align: 'center',
                    sx: {
                      fontWeight: 600,
                      backgroundColor: 'background.paper',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      textAlign: 'center',
                      verticalAlign: 'middle'
                    }
                  }, 'Style'),
                  React.createElement(TableCell, {
                    align: 'center',
                    sx: {
                      fontWeight: 600,
                      backgroundColor: 'background.paper',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      textAlign: 'center',
                      verticalAlign: 'middle'
                    }
                  }, 'Color'),
                  React.createElement(TableCell, {
                    align: 'center',
                    sx: {
                      fontWeight: 600,
                      backgroundColor: 'background.paper',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      textAlign: 'center',
                      verticalAlign: 'middle'
                    }
                  }, 'Category'),
                  React.createElement(TableCell, {
                    align: 'center',
                    sx: {
                      fontWeight: 600,
                      backgroundColor: 'background.paper',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      textAlign: 'center',
                      verticalAlign: 'middle'
                    }
                  }, 'Seasons'),
                  React.createElement(TableCell, {
                    align: 'right',
                    sx: {
                      fontWeight: 600,
                      backgroundColor: 'background.paper',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      textAlign: 'right',
                      verticalAlign: 'middle'
                    }
                  }, 'SO Qty'),
                  React.createElement(TableCell, {
                    align: 'right',
                    sx: {
                      fontWeight: 600,
                      backgroundColor: 'background.paper',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      textAlign: 'right',
                      verticalAlign: 'middle'
                    }
                  }, 'Dispatch Qty'),
                  React.createElement(TableCell, {
                    align: 'right',
                    sx: {
                      fontWeight: 600,
                      backgroundColor: 'background.paper',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      textAlign: 'right',
                      verticalAlign: 'middle'
                    }
                  }, 'GRC Qty'),
                  React.createElement(TableCell, {
                    align: 'center',
                    sx: {
                      fontWeight: 600,
                      backgroundColor: 'background.paper',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                      textAlign: 'center',
                      verticalAlign: 'middle'
                    }
                  }, 'Status')
                )
              ),
              React.createElement(TableBody, null,
                items.map((item, index) => 
                  React.createElement(TableRow, {
                    key: index,
                    sx: {
                      '&:nth-of-type(even)': {
                        backgroundColor: 'rgba(0, 0, 0, 0.01)'
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)'
                      }
                    }
                  },
                    React.createElement(TableCell, {
                      align: 'center',
                      sx: {
                        borderColor: 'divider',
                        fontSize: '0.875rem',
                        textAlign: 'center',
                        verticalAlign: 'middle'
                      }
                    }, formatValue(item.barcode)),
                    React.createElement(TableCell, {
                      align: 'center',
                      sx: {
                        borderColor: 'divider',
                        fontSize: '0.875rem',
                        textAlign: 'center',
                        verticalAlign: 'middle'
                      }
                    }, formatValue(item.brand)),
                    React.createElement(TableCell, {
                      align: 'center',
                      sx: {
                        borderColor: 'divider',
                        fontSize: '0.875rem',
                        textAlign: 'center',
                        verticalAlign: 'middle'
                      }
                    }, formatValue(item.style)),
                    React.createElement(TableCell, {
                      align: 'center',
                      sx: {
                        borderColor: 'divider',
                        fontSize: '0.875rem',
                        textAlign: 'center',
                        verticalAlign: 'middle'
                      }
                    }, formatValue(item.color)),
                    React.createElement(TableCell, {
                      align: 'center',
                      sx: {
                        borderColor: 'divider',
                        fontSize: '0.875rem',
                        textAlign: 'center',
                        verticalAlign: 'middle'
                      }
                    }, formatValue(item.department)),
                    React.createElement(TableCell, {
                      align: 'center',
                      sx: {
                        borderColor: 'divider',
                        fontSize: '0.875rem',
                        textAlign: 'center',
                        verticalAlign: 'middle'
                      }
                    }, formatValue(item.seasons)),
                    React.createElement(TableCell, {
                      align: 'right',
                      sx: {
                        borderColor: 'divider',
                        fontSize: '0.875rem',
                        textAlign: 'right',
                        verticalAlign: 'middle'
                      }
                    }, formatValue(item.so_qty)),
                    React.createElement(TableCell, {
                      align: 'right',
                      sx: {
                        borderColor: 'divider',
                        fontSize: '0.875rem',
                        textAlign: 'right',
                        verticalAlign: 'middle'
                      }
                    }, formatValue(item.dispatch_qty)),
                    React.createElement(TableCell, {
                      align: 'right',
                      sx: {
                        borderColor: 'divider',
                        fontSize: '0.875rem',
                        textAlign: 'right',
                        verticalAlign: 'middle'
                      }
                    }, formatValue(item.grc_qty)),
                    React.createElement(TableCell, {
                      align: 'center',
                      sx: {
                        borderColor: 'divider',
                        fontSize: '0.875rem',
                        textAlign: 'center',
                        verticalAlign: 'middle'
                      }
                    },
                      React.createElement(Box, {
                        sx: {
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                          height: '100%'
                        }
                      },
                        React.createElement(Chip, {
                          label: formatValue(item.so_item_status || item.status),
                          size: 'small',
                          sx: {
                            backgroundColor: (item.uc_sale_order_item_status === 'DISPATCHED' || item.so_item_status === 'DISPATCHED') ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
                            border: '1px solid',
                            borderColor: 'divider',
                            color: 'text.primary',
                            fontWeight: 500,
                            borderRadius: 2
                          }
                        })
                      )
                    )
                  )
                )
              )
            )
          )
        )
      )
    ),

    // Dispatch Details
    React.createElement(Box, {
      sx: {
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        p: { xs: 3, sm: 4 },
        mb: 4,
        backgroundColor: 'background.paper'
      }
    },
      React.createElement(Typography, {
        variant: 'h6',
        sx: {
          mb: 3,
          fontWeight: 600,
          fontSize: '1.25rem'
        }
      }, 'Dispatch Details'),
      React.createElement(Divider, { sx: { mb: 3 } }),
      React.createElement(InfoRow, { label: 'Dispatch Date', value: soData.DISPATCH_DATE ? formatDate(soData.DISPATCH_DATE) : null }),
      React.createElement(InfoRow, { label: 'Invoice Number', value: soData.INVOICE_NO }),
      React.createElement(InfoRow, { label: 'Dispatch Quantity', value: soData.DISPATCH_QTY }),
      React.createElement(InfoRow, { label: 'AWB Number', value: soData.UC_AWB || soData.DISPATCH_AWB || soData.AWB }),
      React.createElement(InfoRow, { label: 'Courier Name', value: soData.COURIER || soData.COURIER_NAME })
    ),

    // Courier Tracking
    (soData.UC_AWB || soData.DISPATCH_AWB || soData.AWB) && (
      courierData ? React.createElement(Box, { sx: { mb: 3 } },
        React.createElement(CourierTracking, { 
          courierData: courierData,
          courierName: soData.COURIER || soData.COURIER_NAME
        })
      ) : React.createElement(Box, {
        sx: {
          border: '1px solid',
          borderColor: 'warning.main',
          borderRadius: 3,
          p: 3,
          mb: 3,
          backgroundColor: 'rgba(255, 152, 0, 0.05)'
        }
      },
        React.createElement(Typography, {
          variant: 'h6',
          sx: {
            mb: 1,
            fontWeight: 600,
            fontSize: '1.1rem',
            color: 'warning.dark'
          }
        }, '⚠️ Courier Tracking Data Not Available'),
        React.createElement(Typography, {
          variant: 'body2',
          sx: { color: 'text.secondary', mb: 1 }
        }, `AWB: ${soData.UC_AWB || soData.DISPATCH_AWB || soData.AWB}`),
        React.createElement(Typography, {
          variant: 'body2',
          sx: { color: 'text.secondary' }
        }, `Courier: ${soData.COURIER || soData.COURIER_NAME || 'Not specified'}`),
        React.createElement(Typography, {
          variant: 'body2',
          sx: { color: 'text.secondary', mt: 2, fontStyle: 'italic' }
        }, 'Courier tracking data has not been uploaded yet. Please ensure the courier data sync is running.')
      )
    ),

    // POD Section
    (courierData?.podGcsUri) && React.createElement(Box, {
      sx: {
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        p: { xs: 3, sm: 4 },
        mb: 4,
        backgroundColor: 'background.paper'
      }
    },
      React.createElement(Typography, {
        variant: 'h6',
        sx: {
          mb: 3,
          fontWeight: 600,
          fontSize: '1.25rem'
        }
      }, 'Proof of Delivery'),
      React.createElement(Button, {
        variant: 'contained',
        startIcon: React.createElement(VisibilityIcon, null),
        onClick: () => setPodViewerOpen(true),
        sx: {
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          textTransform: 'none',
          borderRadius: 2,
          fontWeight: 500,
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
          }
        }
      }, 'View POD')
    ),

    // GRC Details
    React.createElement(Box, {
      sx: {
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        p: { xs: 3, sm: 4 },
        mb: 4,
        backgroundColor: 'background.paper'
      }
    },
      React.createElement(Typography, {
        variant: 'h6',
        sx: {
          mb: 3,
          fontWeight: 600,
          fontSize: '1.25rem'
        }
      }, 'Goods Receipt Details'),
      React.createElement(Divider, { sx: { mb: 3 } }),
      React.createElement(InfoRow, { label: 'GRC Date', value: soData.GRC_DATE ? formatDate(soData.GRC_DATE) : null }),
      React.createElement(InfoRow, { label: 'GRC Quantity', value: soData.GRC_QTY }),
      soData.GRC_DATE && React.createElement(Chip, {
        label: 'Goods Received',
        sx: {
          mt: 2,
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
          border: '1px solid',
          borderColor: 'divider',
          color: 'text.primary',
          fontWeight: 500,
          borderRadius: 2
        }
      })
    ),

    // POD Viewer Dialog
    React.createElement(PODViewer, {
      open: podViewerOpen,
      onClose: () => setPodViewerOpen(false),
      podGcsUri: courierData?.podGcsUri
    })
  );
};

export default SalesOrderDetail;

